/**
 * PM Multibanner -- carrusel editorial horizontal (slide activo centrado +
 * vecinos parcialmente visibles, loop circular, autoplay con línea de
 * progreso, swipe/drag, flechas desktop).
 *
 * RECONSTRUCCIÓN DE MOTOR: el motor de navegación (loop/wrap, swipe/drag,
 * resize, teclado) ya no es una implementación custom PM -- es Flickity,
 * el mismo asset que Athens ya trae en assets/flickity.pkgd.js
 * (assets/flickity.pkgd.js NUNCA se modifica, copia ni renombra desde
 * aquí: solo se referencia por su URL real de Shopify). Flickity sigue
 * siendo la ÚNICA autoridad de NAVEGACIÓN -- this.flickity.selectedIndex
 * es la única fuente de verdad sobre qué celda física está activa; PM no
 * reimplementa avance/retroceso/wrap ni mantiene un índice paralelo que
 * compita con selectedIndex.
 *
 * RONDA 5 (V5 carrusel infinito full-width con repetición dinámica):
 * el viewport del carrusel ya no tiene techo (assets/pm-multibanner.css,
 * .pm-multibanner-container) y el ancho de cada banner se acota de
 * forma INDEPENDIENTE (--pm-mb-active-w, min(96rem, 56cqi)) -- ver el
 * comentario completo en ese archivo. Como consecuencia, wrapAround
 * necesita más de N=3 celdas reales en ultrawide (auditoría original:
 * assets/flickity.pkgd.js, _getWrapShiftCells/_getGapCells, vendor sin
 * modificar) -- este archivo genera repeticiones FÍSICAS reales del
 * ciclo de banners (computeAndApplyRepeats/buildClone más abajo),
 * calculadas contra dimensiones REALES medidas (nunca window.innerWidth
 * ni valores de viewport asumidos). this.physicalSlides SÍ es un
 * bookkeeping nuevo -- la lista de nodos DOM reales (N originales + sus
 * clones) en el mismo orden que Flickity los ve como celdas -- pero es
 * puramente DERIVADO de selectedIndex (accesibilidad, mapeo
 * físico/lógico), nunca una fuente de verdad paralela de navegación:
 * Flickity sigue decidiendo qué índice está seleccionado y cómo
 * llegar ahí (wrap, animación, drag) exactamente igual que antes.
 *
 * Progressive enhancement: si Flickity no llega a cargar (o este script
 * no se ejecuta), sections/pm-multibanner.liquid + assets/pm-multibanner.css
 * ya funcionan por sí solos como un track con overflow-x:auto +
 * scroll-snap nativo. Este archivo solo agrega la clase
 * pm-multibanner--enhanced tras inicializar Flickity con éxito.
 */
(() => {
  'use strict';

  if (customElements.get('pm-multibanner')) return;

  // ------------------------------------------------------------------
  // Carga de Flickity independiente de cualquier otra sección Athens.
  // Tres casos, en este orden:
  //   1. window.Flickity ya existe (otra sección Athens con Flickity ya
  //      se ejecutó antes en esta misma página) -- se usa esa misma
  //      referencia global, sin volver a cargar ni ejecutar nada.
  //   2. Ya hay una carga PM en curso (dos secciones PM Multibanner en
  //      la misma página, o una reconexión rápida) -- se reutiliza la
  //      MISMA promesa, nunca se dispara una segunda carga en paralelo.
  //   3. Ya existe un <script src="flickity.pkgd.js"> en el documento
  //      (inyectado por una sección nativa como slideshow.liquid o
  //      banner-list.liquid) pero todavía no terminó de ejecutarse --
  //      se espera SU propio evento load/error en vez de inyectar una
  //      segunda etiqueta <script> con la misma URL.
  //   Solo si ninguno de los tres casos aplica se crea una etiqueta
  //   <script> nueva. flickity.pkgd.js es el mismo archivo de Athens en
  //   los tres casos -- nunca una copia ni un segundo bundle.
  // ------------------------------------------------------------------
  var flickityLoadPromise = null;

  function ensureFlickity(scriptUrl) {
    if (window.Flickity) return Promise.resolve(window.Flickity);
    if (flickityLoadPromise) return flickityLoadPromise;

    flickityLoadPromise = new Promise((resolve, reject) => {
      var existing = scriptUrl
        ? document.querySelector('script[src="' + scriptUrl + '"]')
        : null;

      var onLoad = () => {
        if (window.Flickity) resolve(window.Flickity);
        else reject(new Error('pm-multibanner: Flickity no está disponible tras cargar ' + scriptUrl));
      };
      var onError = () => reject(new Error('pm-multibanner: error al cargar ' + scriptUrl));

      if (existing) {
        existing.addEventListener('load', onLoad, { once: true });
        existing.addEventListener('error', onError, { once: true });
        return;
      }

      if (!scriptUrl) {
        reject(new Error('pm-multibanner: falta data-flickity-src'));
        return;
      }

      var script = document.createElement('script');
      script.src = scriptUrl;
      script.defer = true;
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
      document.head.appendChild(script);
    });

    return flickityLoadPromise;
  }

  class PmMultibanner extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;

      // Flickity se inicializa EXCLUSIVAMENTE sobre este elemento --
      // la progress bar (this.progressFill más abajo) es hermana
      // EXTERNA de este mismo elemento en el HTML (ver
      // sections/pm-multibanner.liquid), nunca su descendiente. Esa es
      // la corrección estructural: antes, Flickity se inicializaba
      // sobre un contenedor que también alojaba la progress bar como
      // hijo -- al activarse, Flickity retira del DOM únicamente los
      // hijos que matchean cellSelector y luego agrega su propio
      // .flickity-viewport generado al final de los hijos restantes
      // (confirmado leyendo proto.activate en assets/flickity.pkgd.js),
      // así que la progress bar (el único hijo no removido) terminaba
      // pintándose ANTES que el carrusel -- arriba del banner. Ahora,
      // al no ser descendiente de este host en absoluto, ese
      // reordenamiento de Flickity no puede afectarla nunca.
      this.carousel = this.querySelector('[data-pm-multibanner-carousel]');
      if (!this.carousel) return;

      // Los slides reales se capturan ANTES de inicializar Flickity --
      // Flickity mueve estos mismos nodos (cellSelector) a su
      // .flickity-slider interno; capturarlos ahora evita cualquier
      // ambigüedad posterior sobre "cuáles son los slides reales".
      this.realSlides = Array.prototype.slice.call(
        this.carousel.querySelectorAll(':scope > [data-pm-multibanner-slide]')
      );
      if (!this.realSlides.length) return;

      this._connected = true;

      // RONDA 5 (V5 carrusel infinito full-width): desktopQuery se crea
      // AQUÍ, temprano -- antes de Flickity -- porque
      // computeAndApplyRepeats() (más abajo, llamado ANTES de construir
      // Flickity) ya necesita saber si el viewport actual es desktop
      // para decidir si genera repeticiones físicas del ciclo de
      // banners. bindDraggableByViewport() (dentro de initFlickity)
      // reutiliza esta MISMA instancia de matchMedia -- una sola fuente
      // de verdad de "es desktop", un solo listener 'change' sobre ella.
      this.desktopQuery = window.matchMedia('(min-width: 990px)');

      // Boot token: se incrementa en cada connectedCallback que llega
      // hasta aquí y en cada disconnectedCallback. La promesa de
      // ensureFlickity() (más abajo) captura el valor vigente en este
      // instante; cuando resuelve -- posiblemente después de que el
      // Shopify Customizer haya reemplazado esta sección varias veces
      // seguidas -- compara ese valor contra this._bootToken actual.
      // Si no coincide (esta instancia se desconectó, o volvió a
      // conectarse en un ciclo posterior), la resolución se descarta:
      // ninguna inicialización async perteneciente a un ciclo anterior
      // puede llegar a ejecutar initFlickity() sobre un componente que
      // ya no corresponde a ese ciclo.
      this._bootToken = (this._bootToken || 0) + 1;

      this.progressFill = this.querySelector('[data-pm-multibanner-progress-fill]');
      this.prevBtn = this.querySelector('[data-pm-multibanner-prev]');
      this.nextBtn = this.querySelector('[data-pm-multibanner-next]');

      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.realSlides.length > 1;
      this.intervalMs = (parseFloat(this.dataset.interval) || 7) * 1000;

      this._pauseReasons = new Set();
      this._progressAnim = null;
      // true entre el instante en que PM inicia CUALQUIER navegación
      // (flecha, autoplay->next, drag, Shopify block select) y el
      // siguiente 'settle' de Flickity. Mientras es true,
      // startAutoplayCycle() se niega a crear una Animation nueva --
      // así se garantiza que únicamente settle puede iniciar progreso,
      // sin importar cuántas veces se dispare updatePauseState() en el
      // medio (hover, focus, resize, etc. durante la transición).
      this._navigating = false;
      this.flickity = null;

      // RONDA 5: bookkeeping de repetición física. physicalSlides es la
      // lista COMPLETA de nodos DOM reales que Flickity termina viendo
      // como celdas (los N originales + sus clones, en el orden real
      // del DOM) -- Flickity.selectedIndex sigue siendo la ÚNICA
      // autoridad de navegación (nada de esto reemplaza ni duplica esa
      // fuente de verdad); physicalSlides solo permite traducir ese
      // índice físico hacia accesibilidad/estado visual y de vuelta
      // (ver computeAndApplyRepeats/updateAccessibilityForActive/
      // nearestPhysicalIndexForLogical más abajo). Arranca como copia
      // de realSlides (repeatCount=1, sin clones) hasta el primer
      // cálculo real.
      this._repeatCount = 1;
      this._physicalBuilt = false;
      this.physicalSlides = this.realSlides.slice();
      this._lastLogicalIndex = 0;

      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.reducedMotionQuery.matches;
      this._onReducedMotionChange = () => {
        this.reducedMotion = this.reducedMotionQuery.matches;
        this.updatePauseState();
      };
      this.reducedMotionQuery.addEventListener('change', this._onReducedMotionChange);

      this.bindHoverAndFocus();
      this.bindVisibility();

      // RONDA 5: primer cálculo de repeticiones físicas ANTES de
      // construir Flickity -- así la primera activación (new Flickity(),
      // vía initFlickity() más abajo) ya mide/posiciona el conjunto
      // físico completo desde su primer frame, sin un salto visible de
      // "3 banners -> más banners" después de cargar (sección 10 del
      // ticket: CLS/carga inicial). Los clones, si corresponde, quedan
      // insertados como hijos reales de this.carousel (this.element de
      // Flickity) ANTES de new Flickity(...) -- activate() los
      // descubre igual que a los 3 originales, sin tratamiento
      // especial.
      this.computeAndApplyRepeats();

      var bootToken = this._bootToken;
      ensureFlickity(this.dataset.flickitySrc)
        .then((Flickity) => {
          if (!this._connected || bootToken !== this._bootToken) return;
          this.initFlickity(Flickity);
        })
        .catch(() => {
          // Sin Flickity, el componente permanece en su fallback CSS
          // (scroll-snap nativo) -- no se reintenta indefinidamente ni
          // se construye un segundo motor.
        });
    }

    disconnectedCallback() {
      this._connected = false;
      // Invalida de inmediato cualquier ensureFlickity(...).then() que
      // todavía estuviera pendiente de esta instancia -- aunque
      // this._connected ya lo cubre, el token además protege el caso
      // (excepcional pero real en un custom element) de que este mismo
      // nodo se reconecte más adelante: cuando eso ocurra,
      // connectedCallback incrementará el token de nuevo, así que la
      // promesa vieja seguirá sin coincidir aunque _connected vuelva a
      // ser true.
      this._bootToken = (this._bootToken || 0) + 1;

      if (this._resizeRaf) {
        window.cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = null;
      }

      if (this._resizeSafetyDebounce) {
        window.clearTimeout(this._resizeSafetyDebounce);
        this._resizeSafetyDebounce = null;
      }
      if (this._resizeSafetyRaf) {
        window.cancelAnimationFrame(this._resizeSafetyRaf);
        this._resizeSafetyRaf = null;
      }
      if (this._onWindowResize) {
        window.removeEventListener('resize', this._onWindowResize);
        this._onWindowResize = null;
      }

      this.cancelProgressAnimation();

      if (this._onMouseEnter) this.removeEventListener('mouseenter', this._onMouseEnter);
      if (this._onMouseLeave) this.removeEventListener('mouseleave', this._onMouseLeave);
      if (this._onFocusIn) this.removeEventListener('focusin', this._onFocusIn);
      if (this._onFocusOut) this.removeEventListener('focusout', this._onFocusOut);

      if (this._onVisibilityChange) {
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
      }

      if (this.reducedMotionQuery && this._onReducedMotionChange) {
        this.reducedMotionQuery.removeEventListener('change', this._onReducedMotionChange);
      }

      if (this.desktopQuery && this._onDesktopQueryChange) {
        this.desktopQuery.removeEventListener('change', this._onDesktopQueryChange);
      }

      if (this._onPrevClick && this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrevClick);
      if (this._onNextClick && this.nextBtn) this.nextBtn.removeEventListener('click', this._onNextClick);

      if (this._onBlockSelect) this.removeEventListener('shopify:block:select', this._onBlockSelect);
      if (this._onBlockDeselect) {
        this.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
        this.removeEventListener('shopify:section:deselect', this._onBlockDeselect);
      }
      if (this._onSectionSelect) this.removeEventListener('shopify:section:select', this._onSectionSelect);

      if (this.flickity) {
        // Flickity.destroy() ya remueve por sí mismo su propio listener
        // de resize y TODOS sus propios listeners internos (allOff()) --
        // no hace falta desligar 'settle'/'dragStart'/'dragEnd' a mano
        // antes de llamarlo.
        this.flickity.destroy();
        this.flickity = null;
      }
    }

    // ------------------------------------------------------------------
    // Inicialización de Flickity. Guardia contra doble inicialización:
    // Flickity.data(elem) devuelve una instancia ya existente en ese
    // elemento -- si dos secciones PM Multibanner comparten alguna
    // reconexión inesperada, o connectedCallback se disparase dos veces,
    // nunca se crea una segunda instancia sobre el mismo carrusel.
    // ------------------------------------------------------------------
    initFlickity(Flickity) {
      if (!this._connected) return;

      // Se agrega ANTES de construir/reutilizar Flickity, no después
      // como antes -- ver el comentario completo junto a
      // .pm-multibanner__carousel en assets/pm-multibanner.css: hasta
      // que esta clase existe, ese selector reserva un padding-inline
      // que centra visualmente el primer banner en el fallback sin JS.
      // Si la clase se agregara recién después de new Flickity(...),
      // Flickity mediría el ancho de .carousel CON ese padding todavía
      // puesto (más angosto que el real) y centraría mal desde su
      // primer frame -- el mismo salto lateral que se está corrigiendo,
      // solo que movido a un punto distinto. Sin salto visual entre
      // esta línea y new Flickity(): ambas corren en el mismo tick
      // síncrono, sin ningún repintado del navegador en el medio.
      this.classList.add('pm-multibanner--enhanced');

      var existing = Flickity.data && Flickity.data(this.carousel);
      if (existing) {
        this.flickity = existing;
      } else {
        var hasMultiple = this.realSlides.length > 1;
        this.flickity = new Flickity(this.carousel, {
          cellSelector: '[data-pm-multibanner-slide]',
          cellAlign: 'center',
          wrapAround: hasMultiple,
          draggable: hasMultiple,
          prevNextButtons: false,
          pageDots: false,
          autoPlay: false,
          resize: true,
          accessibility: true,
          initialIndex: 0
        });

        // Flickity mide el tamaño de sus celdas de forma síncrona en el
        // instante exacto de new Flickity(...). Si ese instante cae
        // durante el reemplazo de DOM del Shopify Customizer (el
        // navegador puede no haber terminado de calcular layout para el
        // árbol recién insertado), esa medición puede quedar inválida
        // para siempre -- resize:true solo agrega un listener para
        // FUTUROS resize del navegador, nunca vuelve a medir por su
        // propia cuenta si la primera medición fue mala. Un único
        // requestAnimationFrame inmediatamente después de construir NO
        // es una espera arbitraria tipo setTimeout: rAF garantiza que el
        // navegador ya calculó layout para este frame antes de invocar
        // el callback, así que flickity.resize() vuelve a medir con
        // dimensiones definitivas -- sin depender de que un rerender
        // posterior no relacionado (cambiar otro setting) lo corrija
        // por casualidad.
        this._resizeRaf = window.requestAnimationFrame(() => {
          this._resizeRaf = null;
          if (this.flickity) this.flickity.resize();
        });
      }

      // dragStart es, junto con las flechas y el fin natural del
      // autoplay, una de las rutas que INICIA movimiento -- debe
      // cancelar de inmediato cualquier Animation de progress viva (ver
      // beginNavigation()). No se necesita un listener 'dragEnd'
      // separado para el progress: sea que el arrastre termine en un
      // slide nuevo o vuelva al mismo, Flickity siempre dispara 'settle'
      // al llegar a reposo, que es el único punto que reinicia el ciclo.
      this.flickity.on('settle', (index) => this.handleSettle(index));
      this.flickity.on('dragStart', () => this.beginNavigation());
      // Confirmado leyendo assets/flickity.pkgd.js (proto.resize, comentario
      // "#1177 disable resize behavior when animating or dragging for iOS
      // 15"): Flickity descarta en silencio su propio resize() nativo
      // (this.options.resize:true, listener 'resize' de window ya puesto
      // por Flickity mismo, debounced 150ms) si isAnimating/isDragging es
      // true en ese instante -- y nada vuelve a reintentarlo por su cuenta.
      // 'resize' solo se emite aqui cuando un resize() SI se aplico de
      // verdad -- se usa para mantener _lastMeasuredWidth sincronizado con
      // lo que Flickity ya sabe, evitando forzar un resize redundante en
      // ensureResizeApplied() cuando el propio mecanismo nativo ya
      // funciono.
      this.flickity.on('resize', () => {
        if (this.carousel) this._lastMeasuredWidth = this.carousel.getBoundingClientRect().width;
      });

      // Guard de idempotencia: Flickity.data(elem) (más arriba) ya
      // garantiza que nunca se construye una segunda instancia sobre el
      // mismo carrusel, pero initFlickity() en sí podría, en teoría,
      // volver a ejecutarse (p. ej. si connectedCallback se disparase
      // dos veces). Estos cuatro bind* solo deben correr una única vez
      // -- ninguno de sus listeners captura this.flickity al momento de
      // registrarse, todos lo leen en vivo cuando disparan
      // (this._onPrevClick, this._onBlockSelect, updateDraggableForViewport(),
      // ensureResizeApplied() -- ver cada uno más abajo), así que nunca
      // hace falta re-bindear nada. Sin este guard, una segunda
      // ejecución apilaría un listener de click/resize/editor adicional
      // sobre los botones y sobre window.
      if (!this._bindingsReady) {
        this._bindingsReady = true;
        this.bindArrows();
        this.bindShopifyEditorEvents();
        this.bindDraggableByViewport();
        this.bindResizeSafety();
      }

      // Flickity dispara 'ready' (no 'settle') al activarse -- el primer
      // ciclo de progreso/autoplay se arranca explícitamente aquí, una
      // sola vez, para el estado inicial.
      this.handleSettle(this.flickity.selectedIndex);
    }

    // ------------------------------------------------------------------
    // Único punto que reacciona a "el banner terminó físicamente de
    // asentarse". Gobierna: qué slide se marca visualmente activo, el
    // reinicio de la línea de progreso y el inicio del período de
    // autoplay de este ciclo. Nunca se dispara desde 'change', 'select'
    // ni al llamar next()/previous()/select() -- únicamente desde
    // 'settle'. Es también el ÚNICO punto que libera _navigating --
    // hasta que esto ocurre, startAutoplayCycle() se niega a crear una
    // Animation nueva sin importar cuántas veces se invoque
    // updatePauseState() mientras Flickity todavía se desplaza.
    // ------------------------------------------------------------------
    handleSettle(index) {
      this._navigating = false;
      this.updateAccessibilityForActive(index);
      this.cancelProgressAnimation();
      this.updatePauseState();
    }

    // Único punto de cancelación ante CUALQUIER ruta que inicie
    // movimiento del carrusel (flechas, autoplay natural al terminar su
    // intervalo, drag, Shopify block select). Cancela de inmediato la
    // Animation viva -- si no se cancelara aquí y quedara corriendo
    // hasta el settle correspondiente, una Animation con poco tiempo
    // restante (p. ej. 95% avanzada) podría alcanzar su propio onfinish
    // ANTES de que Flickity termine de moverse, disparando un
    // flickity.next() adicional no solicitado (doble avance) mientras
    // el settle real todavía está pendiente. _navigating=true bloquea
    // además que cualquier updatePauseState() intermedio (hover, focus,
    // resize, etc. mientras Flickity todavía se desplaza) arranque un
    // ciclo nuevo por su cuenta -- solo handleSettle() puede liberarlo.
    beginNavigation() {
      this._navigating = true;
      this.cancelProgressAnimation();
    }

    // Flickity no gestiona tabindex por slide (solo tabIndex a nivel de
    // contenedor, vía accessibility:true) -- se conserva esta gestión
    // explícita para que solo el CTA del slide central sea alcanzable
    // por teclado.
    //
    // Dependencia real y demostrada del <a> de la tarjeta (única
    // encontrada en todo el archivo): esta función leía
    // .pm-multibanner__card y comprobaba card.tagName === 'A' -- válido
    // mientras la tarjeta era el enlace real. Ahora que la navegación
    // vive exclusivamente en .pm-multibanner__cta (ver
    // sections/pm-multibanner.liquid: <a> cuando el block tiene link,
    // <div> decorativo si no), esa comprobación debe hacerse sobre el
    // CTA -- la tarjeta ya no es nunca un <a> y ese chequeo quedaría
    // permanentemente inerte si no se actualizaba.
    //
    // RONDA 5 (renombrado desde markActiveSlide): opera sobre
    // this.physicalSlides (N originales + sus clones), no solo sobre
    // los 3 originales -- index aquí es SIEMPRE un índice FÍSICO
    // (Flickity.selectedIndex real), nunca uno lógico. Comportamiento
    // de los 3 slides ORIGINALES sin cambios respecto de antes (solo
    // tabindex del CTA, nunca aria-hidden -- ver comentario superior).
    // Los clones (sección 7 del ticket: "no debe existir contenido
    // accesible duplicado") SÍ reciben aria-hidden cuando no son la
    // ocurrencia física activa, y se les retira por completo en cuanto
    // SÍ lo son -- en cada momento existe exactamente una instancia
    // (original o clon, la que corresponda físicamente) totalmente
    // accesible de la campaña seleccionada, nunca ninguna copia
    // "fantasma" adicional.
    updateAccessibilityForActive(physicalIndex) {
      var slides = (this.physicalSlides && this.physicalSlides.length) ? this.physicalSlides : this.realSlides;
      var originalCount = this.realSlides.length;
      if (originalCount > 0) {
        this._lastLogicalIndex = ((physicalIndex % originalCount) + originalCount) % originalCount;
      }
      slides.forEach((slide, i) => {
        var isActive = i === physicalIndex;
        slide.setAttribute('data-active', isActive ? 'true' : 'false');
        var isClone = slide.hasAttribute('data-pm-multibanner-clone');
        if (isClone) {
          // Clones: aria-hidden + tabindex del CTA, vía los mismos
          // helpers que usa buildClone() para el estado inicial -- una
          // sola fuente de verdad de "qué significa ocultar/mostrar un
          // clon de accesibilidad".
          if (isActive) this.showInAccessibilityTree(slide);
          else this.hideFromAccessibilityTree(slide);
        } else {
          // Originales: comportamiento sin cambios respecto de antes
          // (solo tabindex del CTA, nunca aria-hidden).
          var cta = slide.querySelector('.pm-multibanner__cta');
          if (cta && cta.tagName === 'A') {
            if (isActive) cta.removeAttribute('tabindex');
            else cta.setAttribute('tabindex', '-1');
          }
        }
      });
    }

    // ------------------------------------------------------------------
    // RONDA 5 -- REPETICIÓN FÍSICA DINÁMICA (V5 carrusel infinito
    // full-width). Calcula, a partir de dimensiones REALES medidas en
    // runtime (nunca window.innerWidth ni --pm-mb-active-w/--pm-mb-gap
    // releídos como texto), cuántas veces debe repetirse el ciclo
    // completo de banners originales en el DOM para que wrapAround de
    // Flickity (options.wrapAround:true, vendor sin modificar) tenga
    // siempre celdas reales de sobra a cada lado. Causa raíz real (ver
    // assets/flickity.pkgd.js, proto._getWrapShiftCells/_getGapCells,
    // auditado sin modificar): cada lado (beforeShiftCells/
    // afterShiftCells) consume celdas reales -- desde el final y desde
    // el inicio de this.cells respectivamente -- hasta cubrir la mitad
    // de this.size.innerWidth cada uno; con pocas celdas reales y una
    // celda ancha, ambos recorridos pueden solaparse (la causa
    // exacta identificada en la auditoría original de ultrawide).
    //
    // Solo aplica en desktop (>=990px, gate real vía this.desktopQuery,
    // creado en connectedCallback) -- en mobile/tablet no se genera
    // ninguna copia, se conserva exactamente el comportamiento actual
    // (scroll-snap nativo / Flickity sin repetición), tal como pide el
    // ticket.
    // ------------------------------------------------------------------
    computeRepeatCount(originalCount, innerWidth, cellOuterWidth) {
      if (originalCount <= 1 || !innerWidth || !cellOuterWidth) return 1;
      // +1 celda de margen de seguridad por lado -- evita el caso
      // límite exacto (gapX que termina en 0 justo en el borde de una
      // celda) que fue la causa concreta del conflicto original.
      var cellsNeededPerSide = Math.ceil((innerWidth / 2) / cellOuterWidth) + 1;
      // + un ciclo original completo de margen adicional, para que
      // quede espacio de sobra para navegación/drag sin operar justo
      // en el mínimo matemático.
      var totalNeeded = (cellsNeededPerSide * 2) + originalCount;
      var repeatCount = Math.ceil(totalNeeded / originalCount);
      // Techo defensivo (sección 4 del ticket: "evitar copias
      // innecesarias que perjudiquen rendimiento") ante una medición
      // anómala -- con las dimensiones reales de este componente
      // (banner de hasta 96rem/960px + gap, hasta 3840px de viewport)
      // el resultado real nunca se acerca a este techo, ver reporte.
      return Math.min(repeatCount, 12);
    }

    computeAndApplyRepeats() {
      if (!this.carousel || !this.realSlides.length) return;

      if (!this.desktopQuery || !this.desktopQuery.matches) {
        // Mobile/tablet: sin repetición. Si veníamos de desktop (resize
        // hacia abajo cruzando el breakpoint), se retiran los clones
        // existentes y se vuelve a la lista física = solo originales.
        if (this._repeatCount !== 1 || !this._physicalBuilt) {
          this.applyPhysicalSlides(this.realSlides.slice(), 1);
        }
        return;
      }

      var originalCount = this.realSlides.length;
      var sample = this.realSlides[0];
      // La celda YA incluye el gap completo (ver assets/pm-multibanner
      // .css, .pm-multibanner__slide: width: calc(var(--pm-mb-active-w)
      // + var(--pm-mb-gap))) -- este ancho medido es exactamente el
      // "ancho exterior de celda" real, sin necesitar sumar nada más.
      var cellOuterWidth = sample.getBoundingClientRect().width;
      var innerWidth = this.carousel.getBoundingClientRect().width;
      var repeatCount = this.computeRepeatCount(originalCount, innerWidth, cellOuterWidth);

      if (repeatCount === this._repeatCount && this._physicalBuilt) return;

      var physical = [];
      for (var r = 0; r < repeatCount; r++) {
        for (var i = 0; i < originalCount; i++) {
          physical.push(r === 0 ? this.realSlides[i] : this.buildClone(this.realSlides[i], r, i));
        }
      }
      this.applyPhysicalSlides(physical, repeatCount);
    }

    // Inserta/retira los nodos clon reales en el DOM y actualiza el
    // bookkeeping físico. Limpieza defensiva SIEMPRE al inicio (retira
    // TODOS los clones existentes antes de insertar los nuevos) --
    // garantiza idempotencia real sin importar el estado previo, sin
    // acumular nodos aunque este método se invoque varias veces
    // seguidas o tras una reconexión inesperada del custom element.
    applyPhysicalSlides(physicalArray, repeatCount) {
      this.removeExistingClones();

      var flickityActive = this.flickity && this.flickity.isActive;
      // Antes de activarse, Flickity todavía no movió los slides reales
      // a this.flickity.slider -- los clones deben insertarse como
      // hijos directos de this.carousel (this.element de Flickity) para
      // que activate()/_filterFindCellElements los descubra junto con
      // los originales. Ya activo, las celdas reales viven dentro de
      // this.flickity.slider -- ahí deben insertarse los clones para
      // que reloadCells() (this._makeCells(this.slider.children)) los
      // encuentre.
      var host = flickityActive ? this.flickity.slider : this.carousel;
      physicalArray.forEach((node) => {
        if (node.hasAttribute('data-pm-multibanner-clone')) host.appendChild(node);
      });

      this.physicalSlides = physicalArray;
      this._repeatCount = repeatCount;
      this._physicalBuilt = true;

      if (flickityActive) {
        if (this.flickity.isDragging) return; // no interrumpir un arrastre en curso
        this.flickity.reloadCells();
        // reloadCells() no reposiciona el slider por sí solo -- sin
        // esto, Flickity quedaría "activo" sobre un índice físico que
        // ya no corresponde al mismo banner que se veía un instante
        // antes. Se reposiciona INSTANTÁNEO (isInstant:true) sobre la
        // ocurrencia física más cercana de la misma campaña lógica.
        var nearest = this.nearestPhysicalIndexForLogical(this._lastLogicalIndex);
        this.flickity.select(nearest, false, true);
        this.updateAccessibilityForActive(nearest);
      } else {
        this.updateAccessibilityForActive(0);
      }
    }

    removeExistingClones() {
      if (!this.carousel) return;
      var existing = this.carousel.querySelectorAll('[data-pm-multibanner-clone]');
      existing.forEach((node) => {
        if (node.parentNode) node.parentNode.removeChild(node);
      });
    }

    // Clona un slide ORIGINAL completo. Cada clon queda marcado
    // (data-pm-multibanner-clone) para que removeExistingClones()/
    // updateAccessibilityForActive() puedan identificarlo sin
    // ambigüedad, y oculto de accesibilidad por defecto (sección 7 del
    // ticket) hasta que updateAccessibilityForActive() decida si esta
    // ocurrencia física concreta es la activa.
    buildClone(originalNode, repeatIndex, sourceIndex) {
      var clone = originalNode.cloneNode(true);
      clone.setAttribute('data-pm-multibanner-clone', 'true');
      clone.setAttribute('data-clone-source-index', String(sourceIndex));
      clone.setAttribute('data-index', String((repeatIndex * this.realSlides.length) + sourceIndex));
      this.hideFromAccessibilityTree(clone);
      // IDs únicos: robustez ante cualquier id/for/aria-*-by que exista
      // hoy o se agregue en el futuro dentro del slide -- el markup
      // actual (sections/pm-multibanner.liquid) no declara ningún id
      // propio dentro de un slide, pero dos copias del mismo id en el
      // documento violarían accesibilidad igual si eso cambiara.
      var suffix = '-clone' + repeatIndex + '-' + sourceIndex;
      var idElements = clone.querySelectorAll('[id]');
      idElements.forEach((el) => {
        el.id = el.id + suffix;
      });
      return clone;
    }

    hideFromAccessibilityTree(node) {
      node.setAttribute('aria-hidden', 'true');
      var cta = node.querySelector('.pm-multibanner__cta');
      if (cta && cta.tagName === 'A') cta.setAttribute('tabindex', '-1');
    }

    showInAccessibilityTree(node) {
      node.removeAttribute('aria-hidden');
      var cta = node.querySelector('.pm-multibanner__cta');
      if (cta && cta.tagName === 'A') cta.removeAttribute('tabindex');
    }

    // Dado un índice LÓGICO (0..N-1, la campaña real de Shopify),
    // devuelve el índice FÍSICO existente más cercano al
    // selectedIndex actual de Flickity -- usado tanto al reconstruir
    // físicos tras un resize (mantener la misma campaña visible, sin
    // saltar a la copia física 0) como al seleccionar desde el Theme
    // Editor (shopify:block:select siempre referencia uno de los 3
    // nodos ORIGINALES, nunca un clon -- ver bindShopifyEditorEvents).
    nearestPhysicalIndexForLogical(logicalIndex) {
      var slides = (this.physicalSlides && this.physicalSlides.length) ? this.physicalSlides : this.realSlides;
      var originalCount = this.realSlides.length;
      if (originalCount <= 0) return logicalIndex;
      var current = (this.flickity && typeof this.flickity.selectedIndex === 'number')
        ? this.flickity.selectedIndex
        : 0;
      var best = logicalIndex;
      var bestDist = Math.abs(logicalIndex - current);
      for (var i = logicalIndex; i < slides.length; i += originalCount) {
        var dist = Math.abs(i - current);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    // ------------------------------------------------------------------
    // Autoplay + línea de progreso: una sola fuente de tiempo (Web
    // Animations API) sobre la ÚNICA barra de progreso global -- nunca
    // el autoPlay nativo de Flickity (deliberadamente autoPlay:false),
    // para que nunca compitan dos temporizadores por el mismo avance.
    // ------------------------------------------------------------------
    startAutoplayCycle() {
      if (!this.autoplayEnabled || this.reducedMotion || !this.progressFill || !this.flickity || this._navigating) return;

      this._progressAnim = this.progressFill.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: this.intervalMs, easing: 'linear', fill: 'forwards' }
      );
      // El fin natural del intervalo es también una ruta que INICIA
      // movimiento -- pasa por beginNavigation() igual que las flechas,
      // así ninguna otra Animation puede quedar viva y _navigating
      // bloquea que se arranque otro ciclo antes del settle de este
      // avance.
      this._progressAnim.onfinish = () => {
        this.beginNavigation();
        if (this.flickity) this.flickity.next();
      };
    }

    pauseProgressAnimation() {
      if (this._progressAnim) this._progressAnim.pause();
    }

    resumeProgressAnimation() {
      if (this._progressAnim) this._progressAnim.play();
    }

    cancelProgressAnimation() {
      if (this._progressAnim) {
        this._progressAnim.cancel();
        this._progressAnim = null;
      }
    }

    // Fuente única de verdad sobre si el autoplay debe estar corriendo:
    // hover, foco, pestaña oculta, prefers-reduced-motion o interacción
    // del editor de Shopify -- cualquiera de ellas pausa. El arrastre ya
    // no es una razón de pausa: dragStart cancela vía beginNavigation()
    // (ver más abajo), que es un mecanismo distinto y más estricto --
    // bloquea el inicio de un ciclo nuevo hasta el próximo settle, en
    // vez de solo pausar uno existente.
    updatePauseState() {
      if (!this.autoplayEnabled) return;
      var shouldPause = this._pauseReasons.size > 0 || this.reducedMotion;
      if (shouldPause) {
        this.pauseProgressAnimation();
      } else if (this._progressAnim) {
        this.resumeProgressAnimation();
      } else {
        this.startAutoplayCycle();
      }
    }

    addPauseReason(reason) {
      this._pauseReasons.add(reason);
      this.updatePauseState();
    }

    removePauseReason(reason) {
      this._pauseReasons.delete(reason);
      this.updatePauseState();
    }

    // ------------------------------------------------------------------
    // Interacción -- las flechas PM llaman directamente a
    // flickity.previous()/next(), igual que carousel-navigation en
    // assets/carousel-slider.js (Athens). El teclado (ArrowLeft/
    // ArrowRight) ya no lo gestiona PM: accessibility:true delega esa
    // función por completo a Flickity. bindHoverAndFocus() más abajo
    // resuelve por qué esto ya no rompe la progress bar.
    // ------------------------------------------------------------------
    bindArrows() {
      if (this.prevBtn) {
        this._onPrevClick = () => {
          this.beginNavigation();
          this.flickity.previous();
        };
        this.prevBtn.addEventListener('click', this._onPrevClick);
      }
      if (this.nextBtn) {
        this._onNextClick = () => {
          this.beginNavigation();
          this.flickity.next();
        };
        this.nextBtn.addEventListener('click', this._onNextClick);
      }
    }

    // Causa real y confirmada de "la progress bar deja de funcionar al
    // presionar la flecha": un <button> retiene el foco después de un
    // click, y ese foco burbujea como 'focusin' hasta <pm-multibanner>,
    // agregando la razón de pausa 'focus' -- que nada volvía a quitar
    // tras el click, dejando la barra (recién cancelada por
    // handleSettle) sin poder reiniciarse hasta que el foco saliera del
    // componente por accidente.
    //
    // Reauditado (sin usar blur()): la corrección correcta no es forzar
    // blur() tras cada click -- eso perjudicaría a quien navega con
    // teclado, retirándole el indicador visual de foco justo después de
    // activar el control. El mecanismo correcto es distinguir el ORIGEN
    // del foco, exactamente como ya lo hace el propio navegador para
    // decidir si dibuja un anillo de foco: la pseudo-clase
    // :focus-visible. En los navegadores modernos, un <button> enfocado
    // por click de mouse NO matchea :focus-visible (el navegador ya
    // decide que un click no necesita indicador visible), mientras que
    // un <button> enfocado por Tab SÍ la matchea. Filtrando 'focusin'
    // por esa misma condición: un click de mouse en la flecha nunca
    // agrega la razón de pausa 'focus' en primer lugar (nada que limpiar
    // después), mientras que tabular hasta la flecha sigue pausando el
    // autoplay con el anillo de foco intacto -- exactamente el
    // comportamiento esperado en ambos casos. Si el navegador no
    // reconoce :focus-visible (matches() lanzaría un SyntaxError con un
    // selector no soportado), se conserva el comportamiento anterior
    // (pausar ante cualquier foco) como fallback seguro.
    bindHoverAndFocus() {
      this._onMouseEnter = () => this.addPauseReason('hover');
      this._onMouseLeave = () => this.removePauseReason('hover');
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);

      this._onFocusIn = (event) => {
        var isFocusVisible = true;
        try {
          isFocusVisible = event.target.matches(':focus-visible');
        } catch (error) {
          isFocusVisible = true;
        }
        if (!isFocusVisible) return;
        this.addPauseReason('focus');
      };
      this._onFocusOut = (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.removePauseReason('focus');
      };
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);
    }

    bindVisibility() {
      this._onVisibilityChange = () => {
        if (document.hidden) this.addPauseReason('hidden');
        else this.removePauseReason('hidden');
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }

    // shopify:block:select localiza el slide real seleccionado en el
    // editor y le pide a Flickity que se posicione ahí -- flickity.select()
    // anima hacia ese índice y dispara su propio 'settle' al llegar,
    // reiniciando el ciclo de progreso de forma natural, sin ningún
    // camino paralelo para el Customizer.
    // Desactiva el drag con mouse en escritorio, conservando swipe táctil
    // en mobile/tablet -- usando EXCLUSIVAMENTE la API real de Flickity
    // confirmada leyendo assets/flickity.pkgd.js: bindDrag()/unbindDrag()
    // son wrappers reales de Flickity (this.options.draggable = valor;
    // this.updateDraggable();), no métodos inventados. updateDraggable()
    // es lo que agrega/quita la clase is-draggable sobre el propio host
    // -- assets/flickity.css (Athens, sin modificar) gatea el cursor
    // grab/grabbing exclusivamente detrás de esa clase, así que
    // desactivar el drag por esta vía también apaga el cursor sin
    // necesitar ninguna regla CSS propia. matchMedia('(min-width:990px)')
    // es la ÚNICA fuente de verdad de "escritorio o no" -- se sincroniza
    // una vez aquí y de nuevo cada vez que cambia, sin destruir ni
    // reconstruir la instancia de Flickity en ningún caso. Con un solo
    // slide real, el arrastre queda deshabilitado siempre,
    // independientemente del viewport -- igual que el 'draggable:
    // hasMultiple' original de la construcción, más arriba.
    bindDraggableByViewport() {
      // RONDA 5: this.desktopQuery ya fue creado en connectedCallback
      // (antes de Flickity, para computeAndApplyRepeats()) -- se
      // reutiliza esa misma instancia de matchMedia, nunca se crea una
      // segunda.
      this.updateDraggableForViewport();
      this._onDesktopQueryChange = () => {
        this.updateDraggableForViewport();
        // Cruce real del breakpoint desktop/mobile: recalcula
        // repeticiones (mobile no genera copias; desktop sí) con el
        // mismo mecanismo idempotente que usa un resize normal dentro
        // de un mismo modo.
        this.computeAndApplyRepeats();
      };
      this.desktopQuery.addEventListener('change', this._onDesktopQueryChange);
    }

    updateDraggableForViewport() {
      if (!this.flickity) return;
      var shouldDrag = this.realSlides.length > 1 && !this.desktopQuery.matches;
      if (shouldDrag) {
        this.flickity.bindDrag();
      } else {
        this.flickity.unbindDrag();
      }
    }

    // ------------------------------------------------------------------
    // Red de seguridad para la causa real y confirmada de que las slides
    // desaparecen tras un cambio de breakpoint en runtime (mobile ->
    // desktop) sin reload: el resize() nativo de Flickity (this.options.
    // resize: true, ver initFlickity más arriba) puede quedar descartado
    // en silencio por su propia guarda interna
    // ("isAnimating || isDragging", assets/flickity.pkgd.js, proto.resize,
    // comentario "#1177 ... for iOS 15") si el resize debounced de
    // Flickity cae justo mientras el autoplay/una flecha/un drag está en
    // curso -- nada dentro de Flickity vuelve a reintentarlo por su
    // cuenta. Cuando eso ocurre, this.size interno de Flickity (y por lo
    // tanto la posición en X de cada celda) queda calculado con el ancho
    // mobile antiguo, aunque el contenedor ya mida el ancho desktop real
    // por CSS -- de ahí que el alto/espacio del carrusel se vea correcto
    // pero las celdas queden posicionadas fuera de la vista visible. Un
    // reload reconstruye Flickity desde cero a partir del ancho ya
    // correcto, por eso "arregla" el síntoma sin que el código cambie.
    //
    // Esta red NO reemplaza el resize nativo de Flickity (sigue intacto,
    // options.resize:true) -- solo detecta cuándo ese resize nativo no
    // pudo aplicarse de verdad (el ancho real del contenedor sigue sin
    // coincidir con _lastMeasuredWidth, la última vez que Flickity emitió
    // su propio evento 'resize' real) y, en ese caso, espera a que
    // Flickity deje de estar ocupado (mismo criterio exacto que su propia
    // guarda: isAnimating/isDragging) para pedirle resize() una sola vez,
    // usando requestAnimationFrame para sondear ese estado real
    // frame a frame -- se resuelve en cuanto termina la transición en
    // curso (típicamente unos pocos frames), nunca es una espera de
    // duración arbitraria tipo setTimeout. Sin destruir/recrear la
    // instancia existente.
    bindResizeSafety() {
      this._lastMeasuredWidth = this.carousel ? this.carousel.getBoundingClientRect().width : 0;

      this._onWindowResize = () => {
        if (this._resizeSafetyDebounce) window.clearTimeout(this._resizeSafetyDebounce);
        // 160ms: intencionalmente unos milisegundos después del debounce
        // de 150ms que Flickity ya usa para su propio resize nativo
        // (utils.debounceMethod(Flickity, 'onresize', 150)) -- así el
        // intento nativo de Flickity siempre corre primero; esta red solo
        // actúa si, tras eso, el ancho real seguía sin coincidir.
        this._resizeSafetyDebounce = window.setTimeout(() => this.ensureResizeApplied(), 160);
      };
      window.addEventListener('resize', this._onWindowResize);
    }

    ensureResizeApplied() {
      if (!this.flickity || !this.carousel) return;
      // RONDA 5: recalcula repeticiones físicas en cada resize
      // asentado (mismo debounce de 160ms ya existente, sin listener
      // adicional) -- computeAndApplyRepeats() es idempotente por
      // construcción (early-return si el repeatCount no cambió), así
      // que en la inmensa mayoría de los resizes esto es un no-op
      // barato (una medición + una comparación), no una reconstrucción.
      this.computeAndApplyRepeats();
      var currentWidth = this.carousel.getBoundingClientRect().width;
      // Sin cambio real de ancho, o el resize nativo de Flickity ya
      // actualizó _lastMeasuredWidth (vía el listener 'resize' en
      // initFlickity) -- nada que hacer.
      if (!currentWidth || currentWidth === this._lastMeasuredWidth) return;
      this.waitForIdleAndResize();
    }

    waitForIdleAndResize() {
      if (!this.flickity) return;
      if (this.flickity.isAnimating || this.flickity.isDragging) {
        this._resizeSafetyRaf = window.requestAnimationFrame(() => this.waitForIdleAndResize());
        return;
      }
      this._resizeSafetyRaf = null;
      // Mismo método público que ya usa options.resize:true -- nunca se
      // destruye/recrea la instancia. this.flickity.on('resize', ...)
      // (ver initFlickity) actualiza _lastMeasuredWidth cuando esta
      // llamada logre aplicarse.
      this.flickity.resize();
    }

    bindShopifyEditorEvents() {
      this._onBlockSelect = (event) => {
        var slide = event.target.closest && event.target.closest('[data-pm-multibanner-slide]');
        if (!slide) return;
        // RONDA 5: el editor de Shopify solo conoce los N nodos
        // ORIGINALES (nunca las copias físicas, un constructo
        // puramente de runtime que Shopify no renderizó) -- por eso
        // esta búsqueda sigue siendo sobre this.realSlides, sin
        // cambios. Se selecciona la ocurrencia física MÁS CERCANA de
        // esa misma campaña (nearestPhysicalIndexForLogical) en vez de
        // forzar siempre el índice físico 0..N-1 de la primera
        // repetición, para minimizar la distancia/duración de la
        // animación de Flickity.
        var logicalIndex = this.realSlides.indexOf(slide);
        if (logicalIndex === -1) return;
        this.addPauseReason('editor');
        this.beginNavigation();
        this.flickity.select(this.nearestPhysicalIndexForLogical(logicalIndex));
      };
      this._onBlockDeselect = () => this.removePauseReason('editor');
      this._onSectionSelect = () => this.addPauseReason('editor');

      this.addEventListener('shopify:block:select', this._onBlockSelect);
      this.addEventListener('shopify:block:deselect', this._onBlockDeselect);
      this.addEventListener('shopify:section:select', this._onSectionSelect);
      this.addEventListener('shopify:section:deselect', this._onBlockDeselect);
    }
  }

  customElements.define('pm-multibanner', PmMultibanner);
})();
